import store from '@state/store'
import { lazyLoadView } from '@utils/app/lazy-load-routes'
import { setInfo } from '@utils/app/select-info'

export default [
  {
    path: '/settings/device',
    name: 'Device Setup',
    component: () => lazyLoadView(import('@views/settings/settings-device')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('device/fetchDevices', { clearCache: true }),
          store.dispatch('deviceGroups/fetchGroups', { clearCache: true }),
          store.dispatch('deviceTypes/fetchTypes', { clearCache: true }),
          store.dispatch('deviceShifts/fetchShifts', { clearCache: true }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])
          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'Device Setup' } })
          })
      },
    },
  },

  {
    path: '/settings/device/activity',
    name: 'Device Activities',
    component: () => lazyLoadView(import('@views/settings/settings-device-activity')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('deviceActivity/fetchActivities', { clearCache: true }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])
          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'Device Activity Setup' } })
          })
      },
    },
  },

  {
    path: '/settings/device/cycle',
    name: 'Device Cycle',
    component: () => lazyLoadView(import('@views/settings/settings-device-cycle')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('deviceCycle/fetchCycles', {
            clearCache: true,
          }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])
          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'Device Activity Setup' } })
          })
      },
    },
  },
  {
    path: '/settings/device/group',
    name: 'Device Group',
    component: () => lazyLoadView(import('@views/settings/settings-device-group')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('deviceGroups/fetchGroups', {
            clearCache: true,
          }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])

          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'Device Activity Setup' } })
          })
      },
    },
  },

  {
    path: '/settings/device/shift',
    name: 'Device Shift',
    component: () => lazyLoadView(import('@views/settings/settings-device-shift')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('deviceShifts/fetchShifts', {
            clearCache: true,
          }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])
          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'Device Shift Setup' } })
          })
      },
    },
  },

  {
    path: '/settings/device/status',
    name: 'Device Status',
    component: () => lazyLoadView(import('@views/settings/settings-device-status')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('deviceStatus/fetchStatuses', {
            clearCache: true,
          }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])

          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'Device Status Setup' } })
          })
      },
    },
  },

  {
    path: '/settings/device/type',
    name: 'Device Type',
    component: () => lazyLoadView(import('@views/settings/settings-device-type')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('deviceTypes/fetchTypes', {
            clearCache: true,
          }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])

          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'Device Activity Setup' } })
          })
      },
    },
  },

  {
    path: '/settings/actions',
    name: 'Actions Setup',
    component: () => lazyLoadView(import('@views/settings/settings-actions')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('configuration/fetchConfigurations', {
            clearCache: true,
          }),
        ])

          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'Actions Setup' } })
          })
      },
    },
  },

  {
    path: '/settings/analytics',
    name: 'Analytics Settings',
    component: () => lazyLoadView(import('@views/settings/settings-analytics')),
    meta: {
      authRequired: true,
    },
  },

  {
    path: '/settings/api',
    name: 'Open Industry API',
    component: () => lazyLoadView(import('@views/settings/settings-swagger')),
    meta: {
      authRequired: true,
    },
  },

  {
    path: '/settings/users',
    name: 'User Settings',
    component: () => lazyLoadView(import('@views/settings/settings-users')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('usersAdmin/fetchUsers', {
            clearCache: true,
          }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])
          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: 'Login', params: { resource: 'User Setup' } })
          })
      },
    },
  },

  {
    path: '/settings/entitydescription',
    name: 'Entity Description Settings',
    component: () =>
      lazyLoadView(import('@views/settings/settings-device-event-entity-description')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        Promise.all([
          store.dispatch('entityDescription/fetchEntityDescriptions', {
            clearCache: true,
          }),
          store.dispatch('deviceTypes/fetchTypes', {
            clearCache: true,
          }),
          store.dispatch('info/setInfo', setInfo(routeTo.name)),
        ])
          .then(() => {
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({
              name: 'Login',
              params: { resource: 'Device Entity Description Setup' },
            })
          })
      },
    },
  },

  {
    path: '/settings/edge',
    name: 'Edge',
    component: () => lazyLoadView(import('@views/settings/settings-node-red')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('info/setInfo', setInfo(routeTo.name))
        next()
      },
    },
  },
]
